/**
 * `/about-us/leadership-team/<slug>/`: one leader's profile page, one exported page per Contentful
 * `leader` entry. On any other `NEXT_PUBLIC_COMPANY` no page is built.
 *
 * `getStaticPaths` lists the slugs (`src/contentful/leader/getContentfulLeadersSlugs.ts`) and
 * `getStaticProps` fetches each leader in all three languages
 * (`src/contentful/leader/getContentfulLeader.ts`), both at build time. A slug with no entry in
 * any one locale throws there and fails the build. The body and head tags are
 * `components/Leadership/LeadershipProfileContainer.tsx`.
 *
 * The portrait links (`components/Leadership/Team/ContentfulLeaders.tsx`) and the canonical URL
 * (`LeadershipProfileContainer.tsx`) both build `"/about-us/leadership-team/" + slug`, so the
 * route folder and those strings change together.
 * `src/unit-test/pages/about-us/leadership-team/leaderSlug.spec.tsx` pins the paths, the props
 * and the non-Ryerson empty path list.
 */
import React from "react";
import LeadershipProfileContainer from "@components/Leadership/LeadershipProfileContainer";
import HeaderLayout from "@components/NextLayouts/HeaderLayout";
import { getHeaderData } from "@components/NextLayouts/nextUtilities";
import getContentfulLeader from "@contentful/leader/getContentfulLeader";
import getContentfulLeadersSlugs from "@contentful/leader/getContentfulLeadersSlugs";
import { ProcessedContentfulLeaderFieldsByLanguage } from "@contentful/leader/types";
import { allContentfulHeaderCalls, HeaderPageProps } from "@ryerson/frontend.contentful";
import { GetStaticProps } from "next";

/** One path per leader slug on the Ryerson build, none otherwise. Any other slug is a 404. */
export const getStaticPaths = async () => {
	// skip page generation
	if (process.env.NEXT_PUBLIC_COMPANY !== "ryerson") {
		// If company is not ryerson, in local dev (yarn start) next.js will still try to serve the page for any URL but since getStaticProps will not run, the page will break.
		// In production (static export), unknown paths return 404.html — so this is a local dev-only quirk.
		return { paths: [], fallback: false };
	}

	const leaderSlugs = await getContentfulLeadersSlugs();

	return {
		paths: leaderSlugs.map((leaderSlug) => ({
			params: {
				leaderSlug,
			},
		})),
		fallback: false, // returns 404 for all other paths
	};
};

type PageParams = {
	leaderSlug: string;
};

type PageProps = {
	contentfulLeaderByLanguage: ProcessedContentfulLeaderFieldsByLanguage;
};

/** Ryerson build only. Fetches the header and footer data and this leader's profile. */
export const getStaticProps: GetStaticProps<PageProps, PageParams> = async ({ params }) => {
	if (process.env.NEXT_PUBLIC_COMPANY !== "ryerson") {
		return { notFound: true }; // tells Next.js to skip page generation and return 404
	}

	const { props: headerFooterProps } = await allContentfulHeaderCalls();
	return {
		props: {
			...headerFooterProps,
			contentfulLeaderByLanguage: await getContentfulLeader(params?.leaderSlug as string), // type narrowing as we know this is a string
		},
	};
};

/** Renders the leader's profile inside the site header and footer. */
const Page: React.FC<HeaderPageProps & PageProps> = (props) => {
	return (
		<HeaderLayout {...getHeaderData(props)}>
			<LeadershipProfileContainer
				contentfulLeaderByLanguage={props.contentfulLeaderByLanguage}
			/>
		</HeaderLayout>
	);
};

export default Page;
